import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Video, CheckCircle2, AlertTriangle, PenLine, Lock, Clock } from "lucide-react";

/** Public guest attendance page — hit from an emailed one-time signed link.
 *  Route: /meeting/:id/guest?token=<jwt>
 *  Not wrapped in Protected — external attendees do NOT need a hubCMD account.
 */
export default function MeetingGuestAttend() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [state, setState] = useState({ loading: true, info: null, error: "", attended: false, guestName: "" });
  const base = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (!token) {
      setState({ loading: false, error: "Missing invite token — the link may be broken. Ask the meeting host to resend.", info: null });
      return;
    }
    axios.post(`${base}/api/formal-meetings/guest-info`, { token })
      .then((r) => setState({ loading: false, info: r.data, error: "", attended: r.data.already_attended, guestName: r.data.guest.name }))
      .catch((err) => setState({ loading: false, info: null, error: err?.response?.data?.detail || "This invite link is invalid or expired." }));
  }, [token, base]);

  const attend = async () => {
    try {
      const r = await axios.post(`${base}/api/formal-meetings/guest-attend`, { token });
      setState((s) => ({ ...s, attended: true, guestName: r.data.guest_name || s.guestName }));
      toast.success("Attendance recorded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't record attendance");
    }
  };

  if (state.loading) return <Frame><div className="text-[12px] font-mono uppercase tracking-wider text-white/60 animate-pulse">Loading invite…</div></Frame>;

  if (state.error) return (
    <Frame>
      <div className="text-center py-8" data-testid="guest-error">
        <AlertTriangle className="w-10 h-10 text-[#ff4500] mx-auto" />
        <div className="mt-4 font-display font-black text-xl text-white">{state.error}</div>
      </div>
    </Frame>
  );

  const m = state.info.meeting;
  const styleLabel = ({
    management_review: "Monthly Management Review",
    problem_solving: "Problem Solving",
    effectiveness_review: "Effectiveness Review",
    safety: "Safety Stand-Down",
    custom: "Custom",
  })[m.style] || m.style;
  const isClosed = m.status === "closed";

  return (
    <Frame>
      <div className="text-center" data-testid="guest-attend-panel">
        <div className="w-14 h-14 rounded-2xl bg-[#ff4500] text-white flex items-center justify-center mx-auto">
          <Video className="w-6 h-6" />
        </div>
        <div className="mt-3 font-mono text-[10px] uppercase tracking-[0.22em] text-[#ff5f15]">
          // {m.display_id} · {styleLabel}
        </div>
        <div className="mt-1 font-display font-black text-2xl text-white">{m.title}</div>
        <div className="mt-2 text-[12px] text-white/60">
          Hosted by {m.created_by_name} · Meeting {isClosed ? "closed" : "in progress"}
        </div>
      </div>

      <div className="mt-6 bg-white/5 border border-white/15 rounded-2xl p-5">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/55">// You're joining as</div>
        <div className="mt-1 font-display font-bold text-lg text-white">{state.guestName || state.info.guest.name}</div>
        <div className="text-[11px] font-mono text-white/45">{state.info.guest.email} · external guest</div>
      </div>

      {isClosed && !state.attended && (
        <div className="mt-4 bg-[#fff1ea]/10 border border-[#ff4500]/40 rounded-2xl p-4 flex items-start gap-2 text-white">
          <Lock className="w-4 h-4 mt-0.5 text-[#ff4500]" />
          <div className="text-[12px]">This meeting has already been closed. Attendance can no longer be recorded, but you can still see the minutes below.</div>
        </div>
      )}

      {state.attended ? (
        <div className="mt-4 bg-[#1a8754]/20 border border-[#1a8754]/40 rounded-2xl p-4 flex items-center gap-3 text-white" data-testid="guest-attended-banner">
          <CheckCircle2 className="w-5 h-5 text-[#1a8754]" />
          <div>
            <div className="font-display font-bold">You're signed in.</div>
            <div className="text-[11px] font-mono uppercase tracking-wider text-white/55 flex items-center gap-1"><Clock className="w-3 h-3" /> Attendance recorded · signed as {state.guestName}</div>
          </div>
        </div>
      ) : !isClosed && (
        <button data-testid="guest-attend-button" onClick={attend}
          className="mt-4 w-full bg-[#ff4500] hover:bg-[#cc3500] text-white font-display font-black text-sm uppercase tracking-[0.15em] py-4 rounded-2xl inline-flex items-center justify-center gap-2 transition-colors">
          <PenLine className="w-4 h-4" /> Attend meeting · sign as {state.guestName}
        </button>
      )}

      <div className="mt-6 text-center text-[10px] font-mono uppercase tracking-wider text-white/40">
        // hubCMD guest access · this link is one-time signed for {state.info.guest.email}
      </div>
    </Frame>
  );
}

function Frame({ children }) {
  return (
    <div className="min-h-screen bg-[#0f0f0f] text-white flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md bg-[#1a1a1a] rounded-3xl p-8 border border-white/10 shadow-2xl relative overflow-hidden">
        <div className="absolute -top-20 -right-16 w-56 h-56 rounded-full bg-[#ff4500]/25 blur-3xl pointer-events-none" />
        <div className="relative">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-7 h-7 rounded-lg bg-white/10 flex items-center justify-center">
              <div className="font-display font-black text-[11px]">h<span className="text-[#ff5f15]">C</span></div>
            </div>
            <div className="font-display font-black text-sm tracking-tight">
              <span>hub</span><span className="text-[#ff5f15]">CMD</span>
            </div>
          </div>
          {children}
        </div>
      </div>
    </div>
  );
}
